/**
 * CustomDifficultyDialog — rename the Custom difficulty and set its speed (NJS). Opens on Ctrl + click on
 * the Custom button in the Difficulty panel; it is the desktop editor's F11 "Edit Custom Difficulty
 * Settings" panel, with the same limits (store/customDifficulty.ts). Synth VR only.
 * Follows the Bookmarks panel's look.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEditorStore } from '../../store/editorStore';
import {
  CUSTOM_NAME_MAX, CUSTOM_SPEED_MIN, CUSTOM_SPEED_MAX, sanitizeCustomName,
} from '../../store/customDifficulty';
import { color, font, ui } from '../ui/theme';

export default function CustomDifficultyDialog() {
  const open = useEditorStore(s => s.customDifficultyDialogOpen);
  const setOpen = useEditorStore(s => s.setCustomDifficultyDialogOpen);
  const beatmap = useEditorStore(s => s.beatmap);
  const updateMeta = useEditorStore(s => s.updateMeta);

  const [name, setName] = useState('Custom');
  const [speed, setSpeed] = useState(1);
  const [speedTouched, setSpeedTouched] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  // Seed from the map each time the panel opens, and focus the name.
  useEffect(() => {
    if (!open) return;
    setName(beatmap?.custom_difficulty_name || 'Custom');
    setSpeed(clampSpeed(beatmap?.custom_difficulty_speed ?? 1));
    setSpeedTouched(false);
    setTimeout(() => nameRef.current?.focus(), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  if (!open || !beatmap) return null;

  const step = (d: number) => { setSpeed(v => clampSpeed(v + d)); setSpeedTouched(true); };

  // As the desktop does: an empty name keeps the setting. The speed is only written once the arrows have
  // been used, so renaming alone never rewrites a speed the file carries between the whole numbers.
  const save = () => {
    updateMeta({
      ...(name !== '' ? { custom_difficulty_name: name } : null),
      ...(speedTouched ? { custom_difficulty_speed: speed } : null),
    });
    setOpen(false);
  };

  // In a portal: the sidebar this is mounted in is a containing block for fixed positioning, so the panel
  // would otherwise be laid out inside the sidebar instead of over the window.
  return createPortal(
    <div style={ui.overlay} onClick={() => setOpen(false)}>
      <div style={dialogStyle} onClick={e => e.stopPropagation()}>
        <div style={{ ...ui.dialogHeader, padding: '12px 16px' }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: color.ink }}>Custom Difficulty</span>
          <button style={ui.closeBtn} onClick={() => setOpen(false)}>{'✕'}</button>
        </div>
        <div style={bodyStyle}>
          <label style={fieldStyle}>
            <span style={labelStyle}>Name</span>
            <input
              ref={nameRef}
              data-testid="custom-diff-name"
              value={name}
              placeholder="Custom"
              onChange={e => setName(sanitizeCustomName(e.target.value))}
              onKeyDown={e => { if (e.key === 'Enter') save(); }}
              style={ui.input}
            />
            <span style={hintStyle}>Letters and numbers only, up to {CUSTOM_NAME_MAX} characters.</span>
          </label>
          <div style={fieldStyle}>
            <span style={labelStyle}>NJS</span>
            <div style={stepperStyle}>
              <button
                data-testid="custom-diff-speed-down"
                style={arrowBtnStyle}
                disabled={speed <= CUSTOM_SPEED_MIN}
                onClick={() => step(-1)}
                aria-label="Decrease NJS"
              >{'◀'}</button>
              <span data-testid="custom-diff-speed" style={speedBoxStyle}>{speed}</span>
              <button
                data-testid="custom-diff-speed-up"
                style={arrowBtnStyle}
                disabled={speed >= CUSTOM_SPEED_MAX}
                onClick={() => step(1)}
                aria-label="Increase NJS"
              >{'▶'}</button>
            </div>
            <span style={hintStyle}>{CUSTOM_SPEED_MIN}, {CUSTOM_SPEED_MIN + 1} or {CUSTOM_SPEED_MAX}.</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button style={cancelBtnStyle} onClick={() => setOpen(false)}>Cancel</button>
            <button style={saveBtnStyle} onClick={save}>Save</button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const clampSpeed = (v: number) => Math.min(CUSTOM_SPEED_MAX, Math.max(CUSTOM_SPEED_MIN, Math.round(v)));

const dialogStyle: React.CSSProperties = { ...ui.dialog, width: '90%', maxWidth: 360 };

const bodyStyle: React.CSSProperties = { padding: 16, display: 'flex', flexDirection: 'column', gap: 12 };

const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };

const labelStyle: React.CSSProperties = {
  fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
  color: color.accentSoft, fontFamily: font.sans,
};

const stepperStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8 };

const arrowBtnStyle: React.CSSProperties = {
  width: 28, height: 28, borderRadius: 8, padding: 0, cursor: 'pointer',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  color: color.inkDim, fontSize: 11,
};

const speedBoxStyle: React.CSSProperties = {
  ...ui.input, width: 36, textAlign: 'center', display: 'inline-block', boxSizing: 'border-box', fontWeight: 700,
};

const hintStyle: React.CSSProperties = { fontSize: 10, color: color.inkFaint, fontFamily: font.sans };

const cancelBtnStyle: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 999,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  color: color.inkDim, fontFamily: font.sans, fontSize: 11, fontWeight: 600, cursor: 'pointer',
};

const saveBtnStyle: React.CSSProperties = {
  padding: '8px 18px', borderRadius: 999,
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)',
  background: 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.3), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.18))',
  color: 'var(--accent)', fontFamily: font.sans, fontSize: 11, fontWeight: 700, cursor: 'pointer',
};
